/**
 * Pure view helpers for the Squares surfaces. Nothing here decides a rule:
 * ownership, numbers, lock state, limits and winners all arrive resolved from
 * contest-api. These only lay the server's answer out for the screen.
 */
import { squareKeyFor } from "@sportsmet/contest-engine";

import { formatKickoffWithDate } from "../scores/kickoffLabel";
import { SQUARES_PREVIEW_TBD, SQUARES_UNKNOWN_DIGIT } from "./squaresCopy";
import type { typeSquaresGridData, typeSquaresSquare } from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

export function squaresTeamName(
  teamsById: ReadonlyMap<string, typeSquaresTeam>,
  teamId: string | null | undefined,
): string {
  if (teamId === null || teamId === undefined || teamId === "") {
    return SQUARES_PREVIEW_TBD;
  }
  return teamsById.get(teamId)?.name ?? SQUARES_PREVIEW_TBD;
}

export function squaresTeamShort(
  teamsById: ReadonlyMap<string, typeSquaresTeam>,
  teamId: string | null | undefined,
): string {
  if (teamId === null || teamId === undefined || teamId === "") {
    return SQUARES_PREVIEW_TBD;
  }
  return teamsById.get(teamId)?.abbreviation ?? SQUARES_PREVIEW_TBD;
}

/** "Buccaneers at Lions". Away first, the way a schedule reads. */
export function squaresMatchupLabel(input: {
  awayTeamId: string | null | undefined;
  homeTeamId: string | null | undefined;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
}): string {
  return `${squaresTeamName(input.teamsById, input.awayTeamId)} at ${squaresTeamName(input.teamsById, input.homeTeamId)}`;
}

/**
 * The name printed outside the board for one axis (ISSUE-1270). Team names
 * and Team / Opponent axes read the team the server resolved for that axis
 * ("Chicago Bears", or "CHI" when short), so the board matches the setup's
 * "Chicago Bears and Seattle Seahawks on the axes". Every other axis, or a
 * team the league list does not know yet, keeps the server's label.
 */
export function squaresAxisName(input: {
  axesKind: string;
  label: string;
  teamId: string | null | undefined;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
  isShort?: boolean;
}): string {
  const isTeamAxis =
    input.axesKind === "teamNames" || input.axesKind === "teamOpponent";
  if (!isTeamAxis || input.teamId === null || input.teamId === undefined) {
    return input.label;
  }
  const team = input.teamsById.get(input.teamId);
  if (team === undefined) return input.label;
  return input.isShort === true ? team.abbreviation : team.name;
}

/** "Sun, Oct 20 · 1:00 PM" in the viewer's own zone. */
export function squaresKickoffLabel(input: {
  kickoffAt: string | null | undefined;
  timeZone: string;
}): string {
  if (input.kickoffAt === null || input.kickoffAt === undefined) return "";
  return formatKickoffWithDate({
    kickoffAt: input.kickoffAt,
    timeZone: input.timeZone,
  });
}

/** True once a game can carry a real score: it is under way or over. */
export function squaresHasGameScore(status: string): boolean {
  return status === "inProgress" || status === "final";
}

/** "BUF 21 · MIA 17", away first, or null before the game has a score. */
export function squaresScoreLine(input: {
  awayTeamId: string;
  homeTeamId: string;
  awayScore?: number | null | undefined;
  homeScore?: number | null | undefined;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
}): string | null {
  if (
    input.awayScore === null ||
    input.awayScore === undefined ||
    input.homeScore === null ||
    input.homeScore === undefined
  ) {
    return null;
  }
  return `${squaresTeamShort(input.teamsById, input.awayTeamId)} ${String(input.awayScore)} · ${squaresTeamShort(input.teamsById, input.homeTeamId)} ${String(input.homeScore)}`;
}

/** "Live Q2 6:12" or "Final", from the server's own period and clock labels. */
export function squaresEventStatusLine(input: {
  status: string;
  periodLabel?: string | null | undefined;
  clockLabel?: string | null | undefined;
}): string | null {
  if (input.status === "final") return "Final";
  if (input.status === "canceled") return "Canceled";
  if (input.status === "postponed") return "Postponed";
  if (input.status === "delayed") return "Delayed";
  if (input.status !== "inProgress") return null;
  const parts = ["Live", input.periodLabel, input.clockLabel].filter(
    (part): part is string =>
      part !== null && part !== undefined && part !== "",
  );
  return parts.join(" ");
}

/**
 * The header labels along one axis. A 5-row grid covers two digits per row,
 * so its header reads "3 7". Hidden or undrawn numbers read "?".
 */
export function squaresAxisHeaders(input: {
  numbers: readonly number[] | null | undefined;
  count: number;
}): string[] {
  const numbers = input.numbers;
  if (numbers === null || numbers === undefined || numbers.length === 0) {
    return Array.from({ length: input.count }, () => SQUARES_UNKNOWN_DIGIT);
  }
  const perCell = Math.max(1, Math.floor(numbers.length / input.count));
  return Array.from({ length: input.count }, (_, index) =>
    numbers
      .slice(index * perCell, index * perCell + perCell)
      .map((digit) => String(digit))
      .join(" "),
  );
}

export type typeSquaresBoardCell = {
  squareKey: string;
  row: number;
  col: number;
  square: typeSquaresSquare | null;
  isMine: boolean;
  isWinning: boolean;
};

export type typeSquaresBoardLayout = {
  topHeaders: string[];
  sideHeaders: string[];
  rows: typeSquaresBoardCell[][];
};

export function buildSquaresBoardLayout(input: {
  data: Pick<typeSquaresGridData, "grid" | "squares" | "winners">;
  selectedEventId: string | null;
}): typeSquaresBoardLayout {
  const { grid, squares, winners } = input.data;
  const byKey = new Map<string, typeSquaresSquare>();
  for (const square of squares) byKey.set(square.squareKey, square);
  const winningKeys = new Set<string>();
  for (const winner of winners) {
    if (
      input.selectedEventId !== null &&
      winner.eventId !== input.selectedEventId
    ) {
      continue;
    }
    if (winner.squareKey !== null && winner.squareKey !== undefined) {
      winningKeys.add(winner.squareKey);
    }
  }
  const rows: typeSquaresBoardCell[][] = [];
  for (let row = 0; row < grid.rowCount; row += 1) {
    const cells: typeSquaresBoardCell[] = [];
    for (let col = 0; col < grid.colCount; col += 1) {
      const squareKey = squareKeyFor({ row, col });
      const square = byKey.get(squareKey) ?? null;
      cells.push({
        squareKey,
        row,
        col,
        square,
        isMine: square?.isMine ?? false,
        isWinning: winningKeys.has(squareKey),
      });
    }
    rows.push(cells);
  }
  return {
    topHeaders: squaresAxisHeaders({
      numbers: grid.colNumbers,
      count: grid.colCount,
    }),
    sideHeaders: squaresAxisHeaders({
      numbers: grid.rowNumbers,
      count: grid.rowCount,
    }),
    rows,
  };
}

/**
 * Polling cadence for the board (ISSUE-1427). The server decides it per grid
 * state and answers it as grid-get's refreshIntervalMs; null or a missing or
 * non-positive value stops the poll. False stops the poll.
 */
export function squaresBoardPollMs(
  refreshIntervalMs: number | null | undefined,
): number | false {
  if (typeof refreshIntervalMs !== "number" || refreshIntervalMs <= 0) {
    return false;
  }
  return refreshIntervalMs;
}

/**
 * What a board poll can change that other reads also show (QA round 1,
 * ISSUE-1427): the grid's state and who holds which square. The pool header
 * line and the pool card ("Grid open · 0 of 2 squares"), the grid list's
 * state chip, and the Assign sheet's member counts come from other queries,
 * so when this signature moves between two polls the tab refreshes them.
 * Null while there is no board yet.
 */
export function squaresBoardSignature(
  data: Pick<typeSquaresGridData, "grid" | "squares"> | null | undefined,
): string | null {
  if (data === null || data === undefined) return null;
  const holders = data.squares
    .map(
      (square) => `${square.squareKey}=${square.ownerKind}:${square.ownerId}`,
    )
    .sort()
    .join(",");
  return `${data.grid.gridId}|${data.grid.gridState}|${holders}`;
}

/**
 * The letters a held square shows: at most two, so they fit a 28px square on
 * a phone (ISSUE-1270 QA round 3). The server already sends two letters for
 * members; a guest's stored initials or a missing value are cut down or
 * filled in from the display name here, never left blank.
 */
export function squaresCellInitials(square: {
  initials?: string | null | undefined;
  displayName?: string | null | undefined;
}): string {
  const fromInitials = (square.initials ?? "")
    .replace(/\s+/g, "")
    .slice(0, 2)
    .toUpperCase();
  if (fromInitials !== "") return fromInitials;
  const fromName = (square.displayName ?? "")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .filter((letter) => letter !== "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return fromName === "" ? "?" : fromName;
}

/** Two letters from a guest's name, editable afterward. */
export function squaresInitialsFromName(input: {
  firstName: string;
  lastName: string;
}): string {
  const first = input.firstName.trim().charAt(0);
  const last = input.lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase();
}

/** Keeps at most two letters, uppercased. */
export function squaresCleanInitials(value: string): string {
  return value
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 2)
    .toUpperCase();
}

export function squaresMatchesSearch(name: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return name.toLowerCase().includes(needle);
}

/** A fresh idempotency key for Add a grid and Start a new season. */
export function squaresTransactionId(): string {
  return globalThis.crypto.randomUUID();
}
